import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Layers, Globe, Puzzle } from "lucide-react";
import { useSiteSettings } from "@/data/useSiteSettings";
import { PRODUCTS_SHOWCASE_DEFAULT, mergeSection } from "@/data/homepageSectionDefaults";

// Icons + accent colors stay hardcoded and map to card slots by position.
const ICON_META = [
  { icon: Layers, color: "sky" },
  { icon: Globe,  color: "indigo" },
  { icon: Puzzle, color: "emerald" },
];

const colorMap = {
  sky: { bg: "bg-sky-50", icon: "text-sky-600", border: "hover:border-sky-300" },
  indigo: { bg: "bg-indigo-50", icon: "text-indigo-600", border: "hover:border-indigo-300" },
  emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", border: "hover:border-emerald-300" },
};

export default function ProductsShowcase({ data }) {
  const settings = useSiteSettings();
  const merged = mergeSection(PRODUCTS_SHOWCASE_DEFAULT, settings?.homepage?.products_showcase);
  const c = mergeSection(merged, data);

  return (
    <section data-testid="products-showcase" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left - Text + Cards */}
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {c.heading}
            </h2>
            <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-md">
              {c.intro}
            </p>

            <div className="mt-10 space-y-4">
              {c.cards.map((card, i) => {
                const meta = ICON_META[i] || ICON_META[ICON_META.length - 1];
                const colors = colorMap[meta.color];
                const IconComp = meta.icon;
                return (
                  <motion.div
                    key={`${card.title}-${i}`}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1, duration: 0.5 }}
                  >
                    <Link
                      to={card.link}
                      data-testid={`product-card-${card.title.toLowerCase()}`}
                      className={`group block bg-white border border-slate-200 rounded-xl p-5 ${colors.border} hover:shadow-xl hover:-translate-y-1 transition-all duration-300`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center shrink-0`}>
                          <IconComp className={`h-5 w-5 ${colors.icon}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-semibold text-slate-900 mb-1">{card.title}</h3>
                          <p className="text-sm text-slate-500 leading-relaxed">{card.desc}</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-400 mt-1 group-hover:text-sky-600 group-hover:translate-x-1 transition-all shrink-0" />
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative hidden lg:block"
          >
            <div className="rounded-xl overflow-hidden shadow-xl border border-slate-200">
              <img
                src={c.image_url}
                alt={c.heading}
                className="w-full h-auto"
                data-testid="products-showcase-image"
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
